import "../styles/landing.css";
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Menu, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { submitContactForm } from '../lib/contactApi';

const ClimaOneLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'ClimaOne | Traceable Recycling Platform';
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        'content',
        'ClimaOne enables bag-level traceability across the recycling supply chain, with live inventory, quality records, reporting, and verified material tracking.'
      );

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    document.title = 'ClimaOne | End-to-end Traceable Recycling Platform for Food Grade rPET Flakes Supply Chains';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'ClimaOne enables end-to-end traceability in recycling with bag-level tracking of plastic across the supply chain. Monitor inventory, ensure compliance, and access real-time insights with one powerful platform.'
    );
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.setAttribute('name', 'keywords');
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute(
      'content',
      'plastic traceability, recycling platform, supply chain transparency, rPET tracking, sustainability software, recycling traceability system, plastic waste management, compliance reporting, circular economy technology, ClimaOne'
    );

    window.addEventListener('scroll', handleScroll);

    const scrollToContactFromHash = () => {
      if (window.location.hash !== '#contact') return;

      window.requestAnimationFrame(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'auto' });
      });
    };

    scrollToContactFromHash();
    window.addEventListener('load', scrollToContactFromHash);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('load', scrollToContactFromHash);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      await submitContactForm({
        ...formData,
        source: 'ClimaOne Demo Request',
      });

      setFormSubmitted(true);
      setFormData({ name: '', email: '', organization: '', message: '' });
      setTimeout(() => {
        setFormSubmitted(false);
      }, 3000);
    } catch (error) {
      toast.error('Something went wrong. Please try again or email us directly at info@recircle.in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="climaone-landing">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#F7F1EE] ${isScrolled ? 'backdrop-blur-md shadow-md' : ''}`} style={{ backgroundColor: '#F7F1EE' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="logo-container">
              <img
                src="/assets/climaone-landing/ClimaOne logo (Horizontal) Orginal Dark.png"
                alt="ClimaOne"
                className="h-10 object-contain" />

            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('hero')} className="nav-link">Home</button>
              <button onClick={() => scrollToSection('stakeholders')} className="nav-link">Stakeholders</button>
              <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <a href="https://v2.climaone.in/login" target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary-nav">Login</Button>
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-gray-700"
              onClick={() => setIsMenuOpen(!isMenuOpen)}>

              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen &&
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-4">
                <button onClick={() => scrollToSection('hero')} className="nav-link text-left">Home</button>
                <button onClick={() => scrollToSection('stakeholders')} className="nav-link text-left">Stakeholders</button>
                <button onClick={() => scrollToSection('features')} className="nav-link text-left">Features</button>
                <button onClick={() => scrollToSection('contact')} className="nav-link text-left">Contact</button>
                <a href="https://v2.climaone.in/login" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-primary-nav w-full">Login</Button>
                </a>
              </div>
            </div>
          }
        </div>
      </nav>

      {/* Video Hero Section */}
      <section id="video-hero" className="video-hero-section">
        <div className="video-container">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="hero-video">

            <source src="/assets/climaone-landing/ClimaOne Video-home page hero section.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </section>

      {/* Hero Section */}
      <section id="hero" className="hero-section pt-20 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="hero-content fade-in">
              <h1 className="hero-title mb-6">The Future of Recycling Is Traceable</h1>
              <div className="hero-text space-y-4">
                <p>ClimaOne connects the entire recycling journey. By connecting every stage, the platform enables bag-level traceability for recycled plastic across the entire supply chain.</p>
                <p>Each step is recorded, so every bag of recycled plastic can be traced back to its origin. When recycled plastic enters your supply chain, you know exactly where it came from.</p>
              </div>
              <Button className="btn-primary-large mt-8" onClick={() => scrollToSection('contact')}>
                Book a Live Demo
                <ChevronRight className="ml-2" size={24} />
              </Button>
            </div>
            <div className="hero-image fade-in-delay">
              <img
                src="/assets/climaone-landing/climaone creative 16032026.png"
                alt="ClimaOne Platform"
                className="rounded-xl shadow-2xl w-full h-auto object-cover" />

            </div>
          </div>
        </div>
      </section>

      {/* Key Stakeholders Section */}
      <section id="stakeholders" className="stakeholders-section py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="section-title mb-4">Built for Every Stakeholder in the Recycling Ecosystem</h2>
            <p className="section-subtitle max-w-4xl mx-auto">ClimaOne equips every stakeholder in the recycling supply chain with a dedicated dashboard designed around their role. Whether you collect plastic, process it, purchase rPET flakes, or manage operations, the platform provides real-time visibility across every stage.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Collection Partners */}
            <div className="flip-card">
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="stakeholder-front-row">
                    <div className="stakeholder-icon-compact">
                      <img src="/assets/climaone-landing/Collection Partners.png" alt="Collection Partners" className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="stakeholder-title-compact stakeholder-front-title">Collection Partners</h3>
                  </div>
                </div>
                <div className="flip-card-back">
                  <ul className="stakeholder-list-compact">
                    <li>Faster confirmation of deliveries</li>
                    <li>Improved transparency with plant operators</li>
                    <li>Digital records for dispatches and QC reports</li>
                    <li>Reduced administrative work</li>
                    <li>Better coordination with other stakeholders</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Recycling Plants */}
            <div className="flip-card">
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="stakeholder-front-row">
                    <div className="stakeholder-icon-compact">
                      <img src="/assets/climaone-landing/Recycling plant.png" alt="Recycling Plants" className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="stakeholder-title-compact stakeholder-front-title">Recycling Plants</h3>
                  </div>
                </div>
                <div className="flip-card-back">
                  <ul className="stakeholder-list-compact">
                    <li>Reliable supply chain transparency</li>
                    <li>Full traceability of recycled materials</li>
                    <li>Better coordination with other stakeholders</li>
                    <li>Digital documentation for sustainability reporting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Buyers */}
            <div className="flip-card">
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="stakeholder-front-row">
                    <div className="stakeholder-icon-compact">
                      <img src="/assets/climaone-landing/Buyer.png" alt="Buyers" className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="stakeholder-title-compact stakeholder-front-title">Buyers</h3>
                  </div>
                </div>
                <div className="flip-card-back">
                  <ul className="stakeholder-list-compact">
                    <li>Track incoming dispatches from recycling plants</li>
                    <li>View shipment details and material batch records</li>
                    <li>Access traceability data for received rPET flakes</li>
                    <li>Confirm delivery receipts digitally</li>
                    <li>Maintain records for internal tracking and sustainability reporting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Admin Teams */}
            <div className="flip-card">
              <div className="flip-card-inner">
                <div className="flip-card-front">
                  <div className="stakeholder-front-row">
                    <div className="stakeholder-icon-compact">
                      <img src="/assets/climaone-landing/Team (2).png" alt="Admin Teams" className="w-12 h-12 object-contain" />
                    </div>
                    <h3 className="stakeholder-title-compact stakeholder-front-title">Admin Teams</h3>
                  </div>
                </div>
                <div className="flip-card-back">
                  <ul className="stakeholder-list-compact">
                    <li>Gain a real-time view of operations across the entire ecosystem.</li>
                    <li>Monitor performance & operational efficiency through centralised dashboards</li>
                    <li>Manage stakeholders, user roles, and supplier records from one unified control interface.</li>
                    <li>Access structured reports and traceability records to support compliance needs.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button className="btn-primary" onClick={() => scrollToSection('contact')}>
              Witness ClimaOne in Action
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section id="features" className="features-section py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="section-title mb-4">One platform. Multiple Optimisation Capabilities.</h2>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {/* Feature 1 */}
            <div className="feature-static-card">
              <div className="feature-static-content">
                <div className="feature-static-header">
                  <div className="feature-icon-static">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                      <line x1="12" y1="22.08" x2="12" y2="12" />
                    </svg>
                  </div>
                  <h3 className="feature-static-title">Bag-Level Material Traceability</h3>
                </div>
                <p className="feature-static-description">From the moment raw material is transported from a collection partner to the moment recycled flakes leave the plant, ClimaOne records every stage in a verified digital trail you can follow.</p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="feature-static-card">
              <div className="feature-static-content">
                <div className="feature-static-header">
                  <div className="feature-icon-static">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <h3 className="feature-static-title">Operational Inventory Insights</h3>
                </div>
                <p className="feature-static-description">Gain access to a live view of raw material, batches in process & finished goods so you always know what is in stock and what is ready for dispatch.</p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="feature-static-card">
              <div className="feature-static-content">
                <div className="feature-static-header">
                  <div className="feature-icon-static">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h3 className="feature-static-title">Integrated Quality Assurance</h3>
                </div>
                <p className="feature-static-description">Every production batch carries its own QC reports and technical specifications, allowing stakeholders to access verified quality records directly from the platform.</p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="feature-static-card">
              <div className="feature-static-content">
                <div className="feature-static-header">
                  <div className="feature-icon-static">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                  </div>
                  <h3 className="feature-static-title">Supply Chain Movement Tracking</h3>
                </div>
                <p className="feature-static-description">Track plastic collected by partners as it reaches the plant, and monitor rPET flakes as they move from production lines to downstream buyers.</p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="feature-static-card">
              <div className="feature-static-content">
                <div className="feature-static-header">
                  <div className="feature-icon-static">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="20" x2="18" y2="10" />
                      <line x1="12" y1="20" x2="12" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="14" />
                    </svg>
                  </div>
                  <h3 className="feature-static-title">Operational Data & Compliance Reporting</h3>
                </div>
                <p className="feature-static-description">Transform your daily production activity into structured insights through dashboards & reports that help you monitor performance, verify traceability & stay on top of compliance requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dashboard Screenshots Section */}
      <section id="dashboards" className="dashboards-section py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 fade-in">
            <h2 className="section-title mb-4">See ClimaOne in Action</h2>
            <p className="section-subtitle">Explore dedicated dashboards designed for each stakeholder</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Admin Dashboard */}
            <div className="dashboard-card-large fade-in-up">
              <h3 className="dashboard-card-label-large !text-[#01298A]">Admin Dashboard</h3>
              <div className="dashboard-card-image-large">
                <img
                  src="/assets/climaone-landing/Admin-Dashboard.png"
                  alt="Admin Dashboard"
                  className="dashboard-img-large" />

              </div>
            </div>

            {/* Collection Partner Dashboard */}
            <div className="dashboard-card-large fade-in-up delay-100">
              <h3 className="dashboard-card-label-large !text-[#01298A]">Collection Partner Dashboard</h3>
              <div className="dashboard-card-image-large">
                <img
                  src="/assets/climaone-landing/Partner-Dashboard.png"
                  alt="Collection Partner Dashboard"
                  className="dashboard-img-large" />

              </div>
            </div>

            {/* Recycling Plant Dashboard */}
            <div className="dashboard-card-large fade-in-up delay-200">
              <h3 className="dashboard-card-label-large !text-[#01298A]">Recycling Plant Dashboard</h3>
              <div className="dashboard-card-image-large">
                <img
                  src="/assets/climaone-landing/Plant-Dashboard.png"
                  alt="Recycling Plant Dashboard"
                  className="dashboard-img-large" />

              </div>
            </div>

            {/* Buyer Dashboard */}
            <div className="dashboard-card-large fade-in-up delay-300">
              <h3 className="dashboard-card-label-large !text-[#01298A]">Buyer Dashboard</h3>
              <div className="dashboard-card-image-large">
                <img
                  src="/assets/climaone-landing/converter-dashboard.png"
                  alt="Buyer Dashboard"
                  className="dashboard-img-large" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="contact-content fade-in">
              <h2 className="section-title mb-4">Explore ClimaOne with a Live Demo
              </h2>
              <p className="section-subtitle">Schedule a customised demo to witness the power of ClimaOne firsthand. Our experts will showcase the platform's features, benefits, and how it can simplify your sustainability journey.</p>
            </div>

            {/* Right Side - Form */}
            <div className="contact-form-wrapper fade-in-up">
              <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name</label>
                    <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your full name" />

                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email</label>
                    <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your.email@company.com" />

                  </div>

                  <div className="form-group">
                    <label htmlFor="organization" className="form-label">Organization</label>
                    <Input
                    id="organization"
                    name="organization"
                    type="text"
                    value={formData.organization}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your organization name" />

                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message</label>
                    <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Tell us about your sustainability goals..."
                    rows={4} />

                  </div>

                  <Button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Book a Live Demo!'}
                    <ChevronRight className="ml-2" size={20} />
                  </Button>
                </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="footer-logo">
              <img
                src="/assets/climaone-landing/ClimaOne logo (Horizontal) Original Light.png"
                alt="ClimaOne"
                className="h-8 object-contain" />

            </div>
            <div className="footer-text">
              <p>&copy; 2024 ClimaOne. All rights reserved.</p>
            </div>
            <div className="footer-links flex gap-6">
              <a href="https://v2.climaone.in/login" target="_blank" rel="noopener noreferrer" className="footer-link">Login</a>
              <button onClick={() => scrollToSection('contact')} className="footer-link">Contact</button>
            </div>
          </div>
        </div>
      </footer>
    </div>);

};

export default ClimaOneLanding;
